/**
 * Shared formatting + semantic-colour helpers for the reporting redesign.
 *
 * Pure functions only — no React, no hooks — so they're trivially testable and
 * reusable across every tab. Semantic colour discipline per DESIGN.md §1:
 * emerald = good, amber = attention, red = bad, brand-purple = the single
 * chrome accent. Nothing decorative.
 */

/** Compact integer with locale thousands separators. */
export function formatUnits(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '-';
  return Math.round(value).toLocaleString();
}

/** One-decimal rate (run-rate/day, turns, velocity). */
export function formatRate(value: number | null | undefined, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return '-';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Percentage from a 0..1 ratio. */
export function formatPercent(ratio: number | null | undefined, digits = 0): string {
  if (ratio == null || !Number.isFinite(ratio)) return '-';
  return `${(ratio * 100).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

/**
 * Days-of-cover, floored, with "d" suffix; null = unbounded ("∞").
 *
 * Capped at 365d: a near-zero run-rate (e.g. 1 unit / 30 days against a healthy
 * blank on-hand) otherwise prints a meaningless 5-digit day count. Anything over
 * a year of cover reads as ">365d" — the precise number past that point carries
 * no signal for reordering. `null` (no run-rate) stays "∞".
 */
export const DAYS_OF_COVER_CAP = 365;

export function formatDaysOfCover(days: number | null | undefined): string {
  if (days == null) return '∞';
  if (!Number.isFinite(days)) return '-';
  const floored = Math.floor(days);
  if (floored > DAYS_OF_COVER_CAP) return `>${DAYS_OF_COVER_CAP}d`;
  return `${floored}d`;
}

/** Exact finite cover for audit-oriented detail reports; null remains unbounded. */
export function formatExactDaysOfCover(days: number | null | undefined): string {
  if (days == null) return '∞';
  if (!Number.isFinite(days)) return '-';
  return `${Math.max(0, Math.floor(days)).toLocaleString()}d`;
}

/**
 * Run-rate with adaptive precision for the detailed report. Low-volume blanks
 * (e.g. 1 unit / 30 days = 0.033/day) round to "0.0" at 1 dp, which reads as
 * broken. Below 1/day we show 2 dp so the value stays meaningful; at/above
 * 1/day, 1 dp is plenty.
 */
export function formatRateAdaptive(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '-';
  if (value !== 0 && Math.abs(value) < 1) return formatRate(value, 2);
  return formatRate(value, 1);
}

/** Run-rate expressed as units/week (run-rate/day × 7), one decimal. */
export function formatRatePerWeek(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '-';
  return formatRate(value * 7, 1);
}

/**
 * Days-of-cover RAG band. The real POD stockout signal: a blank with < 7 days
 * of cover is a stockout risk (red), 7–14 is watch (amber), > 14 is healthy
 * (emerald). Null cover (no run-rate) is neutral — not an alarm.
 */
export type RagBand = 'good' | 'attention' | 'bad' | 'neutral';

export function daysOfCoverBand(days: number | null | undefined): RagBand {
  if (days == null) return 'neutral';
  if (days < 7) return 'bad';
  if (days < 14) return 'attention';
  return 'good';
}

/**
 * Inventory-turns band. Higher turns = healthier capital efficiency. Bands are
 * deliberately generous (POD blanks turn slowly): < 2 turns is sluggish capital
 * (amber), >= 4 is strong (emerald), in between is steady (neutral).
 */
export function inventoryTurnsBand(turns: number | null | undefined): RagBand {
  if (turns == null || !Number.isFinite(turns)) return 'neutral';
  if (turns < 2) return 'attention';
  if (turns >= 4) return 'good';
  return 'neutral';
}

/** Delta band: positive = good (emerald), negative = bad (red), zero neutral. */
export function deltaBand(delta: number | null | undefined): RagBand {
  if (delta == null || !Number.isFinite(delta) || delta === 0) return 'neutral';
  return delta > 0 ? 'good' : 'bad';
}

/**
 * Buying action — the On-Order cross-reference of cover vs inbound stock.
 * order_now (red) = at risk with nothing/insufficient on the water; arriving_late
 * (amber) = stock coming but lands after the projected stockout; covered (green)
 * = inbound restores cover in time; ok (neutral) = not at risk.
 */
export type BuyingAction = 'order_now' | 'covered' | 'arriving_late' | 'ok';

export function buyingActionBand(action: BuyingAction): RagBand {
  switch (action) {
    case 'order_now':
      return 'bad';
    case 'arriving_late':
      return 'attention';
    case 'covered':
      return 'good';
    default:
      return 'neutral';
  }
}

/** Sort priority for the buying list — most urgent first. */
export const BUYING_ACTION_PRIORITY: Record<BuyingAction, number> = {
  order_now: 0,
  arriving_late: 1,
  covered: 2,
  ok: 3,
};

/** Tailwind text colour for a RAG band. */
export const RAG_TEXT: Record<RagBand, string> = {
  good: 'text-emerald-600',
  attention: 'text-amber-600',
  bad: 'text-red-600',
  neutral: 'text-muted-foreground',
};

/** Tailwind pill (bg + text + ring) for a RAG band — EditingPill geometry. */
export const RAG_PILL: Record<RagBand, string> = {
  good: 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300/80',
  attention: 'bg-amber-100 text-amber-800 ring-1 ring-amber-300/80',
  bad: 'bg-red-100 text-red-800 ring-1 ring-red-300/80',
  neutral: 'bg-muted text-muted-foreground ring-1 ring-border',
};

/** Recharts fill for a RAG band (hex — recharts needs literal colours). */
export const RAG_FILL: Record<RagBand, string> = {
  good: '#059669', // emerald-600
  attention: '#d97706', // amber-600
  bad: '#dc2626', // red-600
  neutral: '#94a3b8', // slate-400
};

/**
 * Shared chart palette. Brand purple is the single accent; the rest are neutral
 * slate steps so multi-series charts never read as a second accent system.
 */
export const CHART = {
  purple: '#7E69AB', // threadheads-purple — the accent
  purpleSoft: '#B7AAD0',
  emerald: '#059669',
  amber: '#d97706',
  red: '#dc2626',
  slate: '#94a3b8',
  slateDark: '#64748b',
  grid: '#e2e8f0',
  axis: '#94a3b8',
} as const;

/**
 * Donut / categorical palette for the POD vs PAP split and category cuts.
 * Purple leads (the key cut), then neutral slate steps — never a rainbow.
 */
export const CATEGORICAL_FILLS = [
  CHART.purple,
  CHART.slateDark,
  CHART.purpleSoft,
  CHART.slate,
  '#cbd5e1',
  '#475569',
] as const;

// ============================================================================
// Production Cycle-Time Reporting (BLU-684) helpers + tunable thresholds.
//
// These thresholds are provisional — confirm with Ace before treating them as
// operational SLAs. Thread Lab has short history (from June 2026), so every
// consumer must degrade gracefully when the sample is thin.
// ============================================================================

/** An order still open this long or longer is a "straggler" (flagged red). */
export const STRAGGLER_DAYS = 30; // confirm with Ace

/** Below this sample size a median isn't trustworthy — show `n=…`, never fake one. */
export const RELIABLE_MEDIAN_N = 20; // confirm with Ace

/**
 * WIP age-band thresholds in days. `<7` fresh, `7–14` watch, `14–30` stale,
 * `≥30` straggler. Older = worse. Confirm the boundaries with Ace.
 */
export const WIP_AGE_BAND_DAYS = { fresh: 7, watch: 14, stale: 30 } as const; // confirm with Ace

const HOURS_PER_DAY = 24;

/** Days with one decimal + "d" suffix; null / non-finite → em-dash. */
export function formatDays(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return '-';
  if (days === 0) return '0d';
  if (Math.abs(days) < 10) return `${formatRate(days, 1)}d`;
  return `${Math.round(days)}d`;
}

/**
 * Adaptive hours formatter for percentile / age values (all backend cycle-time
 * values are in HOURS). Sub-hour reads as "<1h"; under two days stays in whole
 * hours ("30h"); beyond that switches to days so the eye isn't counting to 300.
 * null / non-finite → em-dash.
 */
export function formatPercentileHours(hours: number | null | undefined): string {
  if (hours == null || !Number.isFinite(hours)) return '-';
  if (hours <= 0) return '0h';
  if (hours < 1) return '<1h';
  if (hours < 48) return `${Math.round(hours)}h`;
  return formatDays(hours / HOURS_PER_DAY);
}

/**
 * WIP age band from an age expressed in HOURS. More age = worse: `<7d` good,
 * `7–14d` neutral (watch), `14–30d` attention (stale), `≥30d` bad (straggler).
 * null age is neutral, not an alarm.
 */
export function wipAgeBand(ageHours: number | null | undefined): RagBand {
  if (ageHours == null || !Number.isFinite(ageHours)) return 'neutral';
  const days = ageHours / HOURS_PER_DAY;
  if (days < WIP_AGE_BAND_DAYS.fresh) return 'good';
  if (days < WIP_AGE_BAND_DAYS.watch) return 'neutral';
  if (days < WIP_AGE_BAND_DAYS.stale) return 'attention';
  return 'bad';
}

/** True when an age in HOURS meets or exceeds the straggler threshold. */
export function isStraggler(ageHours: number | null | undefined): boolean {
  if (ageHours == null || !Number.isFinite(ageHours)) return false;
  return ageHours / HOURS_PER_DAY >= STRAGGLER_DAYS;
}
